import { ReturnPolicyType } from '../constants.js';
export const HEIRLOOM_LISTING_PROFILES = {
    processing: {
        minDays: 0,
        maxDays: 2,
    },
    shipping: {
        originZip: '60622',
        shippingDaysMin: 3,
        shippingDaysMax: 5,
        shippingRate: 0,
    },
    returns: {
        policyType: ReturnPolicyType.STANDARD,
        returnWindowDays: 30,
    },
};
export const sortByOrder = (entries) => Object.entries(entries).sort(([, a], [, b]) => a.order - b.order);
export const summarizeVariations = (variations) => sortByOrder(variations).map(([, variation]) => ({
    name: variation.name,
    numOptions: Object.keys(variation.options).length,
}));
export const isListingOutOfStock = (trackInventory, inventory, variations, combinations) => {
    if (!trackInventory)
        return false;
    const allCombinations = deriveCombinationsList(variations);
    if (allCombinations.length === 0)
        return inventory === 0;
    return allCombinations
        .filter(({ key }) => !combinations[key]?.disabled)
        .every(({ key }) => (combinations[key]?.inventory ?? 0) === 0);
};
export const DEFAULT_COMBINATION = {
    priceCents: null,
    disabled: false,
    inventory: 0,
};
export const getCombinationKey = (optionMap) => Object.entries(optionMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([vId, oId]) => `${vId}:${oId}`)
    .join('|');
export const parseCombinationKey = (key) => {
    if (!key)
        return {};
    return Object.fromEntries(key.split('|').map((s) => {
        const i = s.indexOf(':');
        return [s.slice(0, i), s.slice(i + 1)];
    }));
};
export const deriveCombinationsList = (variations) => {
    const sorted = sortByOrder(variations);
    if (sorted.length === 0)
        return [];
    const optionArrays = sorted.map(([varId, v]) => ({
        varId,
        optIds: sortByOrder(v.options).map(([id]) => id),
    }));
    if (optionArrays.some((a) => a.optIds.length === 0))
        return [];
    let optionMaps = [{}];
    for (const { varId, optIds } of optionArrays) {
        optionMaps = optionMaps.flatMap((existing) => optIds.map((optId) => ({ ...existing, [varId]: optId })));
    }
    return optionMaps.map((optionMap) => ({
        key: getCombinationKey(optionMap),
        optionMap,
    }));
};
export const addVariationToCombinations = (combinations, newVarId, newOptions, newVariationPricesVary) => {
    const newOptionIds = Object.keys(newOptions);
    const existing = Object.entries(combinations);
    if (existing.length === 0) {
        return Object.fromEntries(newOptionIds.map((optId) => [
            getCombinationKey({ [newVarId]: optId }),
            { ...DEFAULT_COMBINATION },
        ]));
    }
    const result = {};
    for (const [key, data] of existing) {
        const optionMap = parseCombinationKey(key);
        for (const optId of newOptionIds) {
            result[getCombinationKey({ ...optionMap, [newVarId]: optId })] = {
                ...data,
                ...(newVariationPricesVary
                    ? { priceCents: null }
                    : {}),
            };
        }
    }
    return result;
};
export const removeVariationFromCombinations = (combinations, varId) => {
    const result = {};
    for (const [key, data] of Object.entries(combinations)) {
        const optionMap = parseCombinationKey(key);
        delete optionMap[varId];
        const newKey = getCombinationKey(optionMap);
        result[newKey] =
            newKey in result ? { ...DEFAULT_COMBINATION } : data;
    }
    return result;
};
export const addOptionToCombinations = (combinations, varId, newOptId) => {
    const result = { ...combinations };
    const seen = new Set();
    for (const key of Object.keys(combinations)) {
        const newKey = getCombinationKey({
            ...parseCombinationKey(key),
            [varId]: newOptId,
        });
        if (!seen.has(newKey)) {
            seen.add(newKey);
            result[newKey] = { ...DEFAULT_COMBINATION };
        }
    }
    return result;
};
export const removeOptionFromCombinations = (combinations, varId, optId) => Object.fromEntries(Object.entries(combinations).filter(([key]) => parseCombinationKey(key)[varId] !== optId));
export const resolveEffectiveCombinationPrice = (optionMap, combinations, variations, basePriceCents) => {
    const key = getCombinationKey(optionMap);
    if (combinations[key]?.priceCents != null)
        return combinations[key].priceCents;
    const sorted = sortByOrder(variations).filter(([, v]) => v.pricesVary);
    for (const [varId, v] of sorted) {
        const price = v.options[optionMap[varId]]?.priceCents ?? null;
        if (price != null)
            return price;
    }
    return basePriceCents;
};
const addPersonalizationCost = (priceCents, costCents) => costCents != null ? priceCents + costCents : priceCents;
const applyPersonalizationCost = (priceCents, personalization) => personalization?.selected
    ? addPersonalizationCost(priceCents, personalization.costCents)
    : priceCents;
export const getSelectionPriceCents = (listing, selectedOptions, personalizationCostCents) => addPersonalizationCost(resolveEffectiveCombinationPrice(selectedOptions, listing.combinations, listing.variations, listing.priceCents) ?? listing.priceCents, personalizationCostCents);
export const getListingDisplayPrice = (variations, combinations, basePriceCents, selectedOptions = {}, personalization = null) => {
    const pricesVary = Object.values(variations).some((v) => v.pricesVary);
    if (!pricesVary) {
        return basePriceCents != null
            ? {
                priceCents: applyPersonalizationCost(basePriceCents, personalization),
                isMinimum: false,
            }
            : null;
    }
    let min = null;
    let max = null;
    for (const { key, optionMap } of deriveCombinationsList(variations)) {
        if (combinations[key]?.disabled)
            continue;
        const matchesSelection = Object.entries(selectedOptions).every(([varId, optId]) => optionMap[varId] == null ||
            optionMap[varId] === optId);
        if (!matchesSelection)
            continue;
        const price = resolveEffectiveCombinationPrice(optionMap, combinations, variations, basePriceCents);
        if (price == null)
            continue;
        if (min === null || price < min)
            min = price;
        if (max === null || price > max)
            max = price;
    }
    return min != null
        ? {
            priceCents: applyPersonalizationCost(min, personalization),
            isMinimum: min !== max,
        }
        : null;
};
export const getListingBasePrice = (variations, combinations, priceCents) => {
    const displayPrice = getListingDisplayPrice(variations, combinations, priceCents);
    return {
        basePriceCents: displayPrice?.priceCents ?? priceCents,
        priceVaries: displayPrice?.isMinimum ?? false,
    };
};
export const getOrderedListingImageUuids = (listing, selectedOptions) => [
    ...new Set([
        ...resolveEffectiveCombinationImages(selectedOptions, listing.variations, listing.variationImages),
        ...listing.imageUuids,
    ]),
];
export const getHeroImageUuid = (listing, selectedOptions) => getOrderedListingImageUuids(listing, selectedOptions)[0] ?? null;
export const resolveEffectiveCombinationImages = (optionMap, variations, variationImages) => {
    const imageVaryingEntries = sortByOrder(getImageVaryingVariations(variations));
    const variationImageUuid = hasVariationImages(variationImages)
        ? variationImages[getCombinationKey(Object.fromEntries(imageVaryingEntries.map(([variationId]) => [
            variationId,
            optionMap[variationId],
        ])))]
        : null;
    const optionImageUuids = imageVaryingEntries.flatMap(([variationId, variation]) => variation.options[optionMap[variationId]]?.imageUuids ??
        []);
    return [
        ...(variationImageUuid ? [variationImageUuid] : []),
        ...optionImageUuids,
    ];
};
export const isVariationOptionDisabled = (varId, optionId, selectedOptions, listing) => {
    const { variations, combinations, trackInventory } = listing;
    const othersSelected = Object.keys(variations)
        .filter((id) => id !== varId)
        .every((id) => selectedOptions[id] != null);
    if (!othersSelected)
        return false;
    const key = getCombinationKey({
        ...selectedOptions,
        [varId]: optionId,
    });
    const combination = combinations[key];
    return ((combination?.disabled ?? true) ||
        (!!trackInventory && (combination?.inventory ?? 0) === 0));
};
const withDefaultOptionsFirst = (variations) => Object.fromEntries(Object.entries(variations).map(([varId, variation]) => [
    varId,
    {
        ...variation,
        options: Object.fromEntries(Object.entries(variation.options).map(([optId, option]) => [
            optId,
            optId === variation.defaultOption
                ? { ...option, order: -1 }
                : option,
        ])),
    },
]));
export const getDefaultOptionSelection = (listing) => {
    const combinations = deriveCombinationsList(withDefaultOptionsFirst(listing.variations));
    if (combinations.length === 0) {
        return { selection: {}, isAvailable: true };
    }
    const firstAvailable = combinations.find(({ optionMap }) => isValidCombinationSelection(optionMap, listing));
    return firstAvailable
        ? { selection: firstAvailable.optionMap, isAvailable: true }
        : {
            selection: combinations[0].optionMap,
            isAvailable: false,
        };
};
export const getDefaultHeroImageUuid = (listing) => getHeroImageUuid(listing, getDefaultOptionSelection(listing).selection);
export const isValidCombinationSelection = (selectedOptions, listing) => {
    const { variations, combinations, trackInventory } = listing;
    const variationIds = Object.keys(variations);
    if (variationIds.length === 0)
        return true;
    for (const varId of variationIds) {
        const optionId = selectedOptions[varId];
        if (!optionId || !variations[varId].options[optionId])
            return false;
    }
    const key = getCombinationKey(selectedOptions);
    const combination = combinations[key];
    if (combination?.disabled ?? true)
        return false;
    return !(trackInventory && (combination?.inventory ?? 0) === 0);
};
export const hasVariationImages = (variationImages) => !!variationImages && Object.keys(variationImages).length > 0;
export const getImageVaryingVariations = (variations) => Object.fromEntries(Object.entries(variations).filter(([, v]) => v.imagesVary));
export const syncVariationImages = (previousVariationImages, variations) => {
    const imageVaryingVariations = getImageVaryingVariations(variations);
    const imageVaryingVariationIds = Object.keys(imageVaryingVariations);
    const imageKeys = deriveCombinationsList(imageVaryingVariations).map(({ key }) => key);
    if (imageVaryingVariationIds.length < 2 || imageKeys.length === 0)
        return null;
    const [firstPreviousImageKey] = Object.keys(previousVariationImages ?? {});
    const previousImageVaryingVariationIds = firstPreviousImageKey
        ? Object.keys(parseCombinationKey(firstPreviousImageKey))
        : [];
    const isSameImageVaryingVariationSet = previousImageVaryingVariationIds.length ===
        imageVaryingVariationIds.length &&
        imageVaryingVariationIds.every((id) => previousImageVaryingVariationIds.includes(id));
    return Object.fromEntries(imageKeys.map((key) => [
        key,
        (isSameImageVaryingVariationSet &&
            previousVariationImages?.[key]) ||
            null,
    ]));
};
export const syncVariationOptions = (combinations, varId, oldOptionIds, newOptionIds) => {
    const oldSet = new Set(oldOptionIds);
    const newSet = new Set(newOptionIds);
    const removed = oldOptionIds.filter((id) => !newSet.has(id));
    const added = newOptionIds.filter((id) => !oldSet.has(id));
    let result = combinations;
    for (const optId of removed) {
        result = removeOptionFromCombinations(result, varId, optId);
    }
    for (const optId of added) {
        result = addOptionToCombinations(result, varId, optId);
    }
    return result;
};
